import React, { useState, useEffect, useRef } from "react";
import { signInWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { updateEmailVerificationStatus } from "../../lib/firestore";

const Login = ({ onSuccess, onSwitchToSignUp }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);

  // Refs to store timer IDs for cleanup
  const cooldownIntervalRef = useRef(null);
  const successTimeoutRef = useRef(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResendEmail = async () => {
    if (!resendEmail || cooldownTime > 0) return
    // Check if we have the password
    if (!formData.password) {
      setErrors({
        submit: "Please enter your password to resend verification email."
      });
      return;
    }

    setResendLoading(true);
    setResendSuccess(false);

    try {
      // Sign in temporarily to get user object
      const userCredential = await signInWithEmailAndPassword(
        auth,
        resendEmail,
        formData.password
      );

      // Send verification email
      await sendEmailVerification(userCredential.user);

      // Sign out immediately
      await auth.signOut();

      // Show success immediately
      setResendSuccess(true);

      // Clear any previous error messages
      setErrors({});

      // Clear any existing timers before starting new ones
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }

      // Wait 2 seconds to show success message, then start cooldown
      successTimeoutRef.current = setTimeout(() => {
        setCooldownTime(60);
        cooldownIntervalRef.current = setInterval(() => {
          setCooldownTime((prev) => {
            if (prev <= 1) {
              clearInterval(cooldownIntervalRef.current);
              cooldownIntervalRef.current = null;
              setResendSuccess(false); // Reset success state when cooldown ends
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        successTimeoutRef.current = null;
      }, 2000); // 2 second delay before countdown starts
    } catch (error) {
      console.error('Resend email error:', error);
      setErrors({
        submit: "Failed to resend email. Please try again."
      });
    } finally {
      setResendLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      await userCredential.user.reload();

      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        // Sign out BEFORE setting error state
        // This prevents the auth listener from redirecting to dashboard
        await auth.signOut();

        // Store email for resend functionality
        setResendEmail(formData.email);

        // Show error message without auto-resending email
        setErrors({
          submit: "Please verify your email before signing in. Check your inbox for the verification link."
        });
        return;
      }


      // Update Firestore to mark email as verified
      try {
        await updateEmailVerificationStatus(userCredential.user.uid, true);
      } catch (firestoreError) {
        console.error("Failed to update email verification status in Firestore:", firestoreError);
        // Don't block login if Firestore update fails
      }

      if (onSuccess) {
        onSuccess(userCredential.user);
      }
    } catch (error) {
      let errorMessage = "Failed to sign in. Please try again.";

      if (error.code === "auth/user-not-found") {
        errorMessage = "No account found with this email address.";
      } else if (error.code === "auth/wrong-password") {
        errorMessage = "Incorrect password. Please try again.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      } else if (error.code === "auth/user-disabled") {
        errorMessage = "This account has been disabled.";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "Too many failed attempts. Please try again later.";
      } else if (error.code === "auth/invalid-credential") {
        errorMessage =
          "Invalid email or password. Please check your credentials.";
      }

      setErrors({ submit: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="auth-card">
        {/* Header */}
        <div className="auth-header">
          <div className="auth-logo-container">
            <img src={`${process.env.PUBLIC_URL}/images/logo-playfast.svg`} alt="PlayFast Logo" className="auth-logo-image" />
          </div>
          <h2 className="auth-title">Welcome Back</h2>
          <p className="auth-subtitle-large">
            Sign in to your PlayFast account
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              className={`form-input ${errors.email ? "form-input-error" : ""}`}
              placeholder="Enter your email"
            />
            {errors.email && <p className="form-error">{errors.email}</p>}
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="current-password"
              className={`form-input ${errors.password ? "form-input-error" : ""
                }`}
              placeholder="Enter your password"
            />
            {errors.password && <p className="form-error">{errors.password}</p>}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="form-submit-error">
              <p className="form-submit-error-text">{errors.submit}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary form-submit-button"
          >
            {isLoading && <div className="spinner spinner-small"></div>}
            {isLoading ? "Signing In..." : "Sign In"}
          </button>

          {/* Resend Verification Email Button */}
          {resendEmail && (
            <div style={{ marginTop: '15px' }}>
              <button
                type="button"
                onClick={handleResendEmail}
                disabled={resendLoading || cooldownTime > 0}
                className="btn form-submit-button"
                style={{
                  backgroundColor: resendSuccess
                    ? '#059669'  // Darker green for success
                    : cooldownTime > 0
                      ? '#e5e7eb'  // Very light gray for disabled
                      : '#6366f1',  // Purple/indigo for active (matches brand)
                  color: resendSuccess ? '#ffffff' : cooldownTime > 0 ? '#9ca3af' : '#ffffff',
                  cursor: cooldownTime > 0 || resendLoading ? 'not-allowed' : 'pointer',
                  opacity: cooldownTime > 0 ? 0.7 : 1,
                  fontWeight: '500',
                  border: cooldownTime > 0 ? '1px solid #d1d5db' : 'none'
                }}
              >
                {resendLoading && <div className="spinner spinner-small"></div>}
                {resendLoading
                  ? "Sending..."
                  : cooldownTime > 0
                    ? `Wait ${cooldownTime}s to resend`
                    : resendSuccess
                      ? "✓ Email Sent Successfully!"
                      : "Resend Verification Email"}
              </button>

              {/* Helper text */}
              {!resendSuccess && cooldownTime === 0 && (
                <p style={{
                  fontSize: '14px',
                  color: '#d97706',
                  marginTop: '10px',
                  textAlign: 'center',
                  lineHeight: '1.5',
                  fontWeight: '500'
                }}>
                  💡 Didn't receive the email? <strong>Check your spam/junk folder</strong> or click above to resend.
                </p>
              )}

              {/* Success message */}
              {resendSuccess && cooldownTime === 0 && (
                <p style={{
                  fontSize: '13px',
                  color: '#059669',
                  marginTop: '10px',
                  textAlign: 'center',
                  fontWeight: '500'
                }}>
                  Check your inbox for the verification link!
                </p>
              )}
            </div>
          )}
        </form>

        {/* Sign Up Link */}
        <div className="auth-footer">
          <p className="auth-footer-text">
            Don't have an account?{" "}
            <button onClick={onSwitchToSignUp} className="auth-link">
              Sign up here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
